import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Languages, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Field, Input, Select, Spinner } from '@zt/ui';
import { LocalePicker, localeName, useContentLocale } from '../components/ContentLocale';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  Category,
  MenusView,
  MenuTranslationDraft,
  PageList,
  PostList,
  ProductPage,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

type Location = 'header' | 'footer';
type Item = MenusView['menus']['header']['items'][number];
type Link = Item['link'];
type Targets = MenusView['menus']['header']['targets'];
const DEPTH: Record<Location, number> = { header: 3, footer: 2 };
const LINK_TYPES: Array<Link['type']> = [
  'none',
  'category',
  'product',
  'products',
  'page',
  'blog',
  'post',
  'url',
];
type PageOption = PageList[number];
type PostOption = PostList[number];

/** Karşılaştırma için anahtar sırasından bağımsız JSON. */
function stable(v: unknown): string {
  return JSON.stringify(v, (_k, x: unknown) =>
    x && typeof x === 'object' && !Array.isArray(x)
      ? Object.fromEntries(
          Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : x,
  );
}

const newItem = (label: string): Item => ({
  id: crypto.randomUUID(),
  label,
  link: { type: 'products' },
  children: [],
});

/** Menüler: üst menü (mega menü, 3 seviye) ve alt bilgi menüsü (sütun başlığı + bağlantılar). */
export function MenusPage() {
  const t = useT();
  const { can } = usePermissions();
  const canEdit = can('content.publish');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const [location, setLocation] = useState<Location>('header');
  const q = useQuery({
    queryKey: ['menus', storeId, locale],
    queryFn: () => api.get<MenusView>(`/v1/admin/stores/${storeId}/menus?locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
    enabled: can('catalog.product.read'),
  });
  // Bağlanabilir sayfalar ve yazılar menünün dilinde (ana sayfanın adresi zaten "/").
  const pageList = useQuery({
    queryKey: ['pages', storeId, locale],
    queryFn: () => api.get<PageList>(`/v1/admin/pages?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  const pageOptions = (pageList.data ?? []).filter((p) => p.kind !== 'home');
  const postList = useQuery({
    queryKey: ['posts', storeId, locale],
    queryFn: () => api.get<PostList>(`/v1/admin/posts?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  const postOptions = postList.data ?? [];
  const [drafts, setDrafts] = useState<Record<Location, Item[]> | null>(null);
  useEffect(() => {
    if (q.data) setDrafts({ header: q.data.menus.header.items, footer: q.data.menus.footer.items });
  }, [q.data]);
  const save = useApiMutation(
    () =>
      api.put<MenusView>(`/v1/admin/stores/${storeId}/menus/${location}`, {
        items: drafts![location],
        locale,
        version: q.data!.menus[location].version,
      }),
    { invalidate: [['menus', storeId]], success: t('admin.menus.saved') },
  );
  if (loading) return <Spinner label={t('common.loading')} />;
  const items = drafts?.[location] ?? [];
  const dirty = !!q.data && !!drafts && stable(drafts[location]) !== stable(q.data.menus[location].items);
  const setItems = (next: Item[]) => setDrafts((d) => (d ? { ...d, [location]: next } : d));
  return (
    <PageFrame
      helpKey="page.menus"
      actions={
        canEdit &&
        q.data && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(undefined)}
            >
              {t('admin.menus.save')}
            </Button>
            <HelpTip k="menus.save" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : !q.data || !drafts ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div
              role="group"
              aria-label={t('admin.menus.location')}
              className="flex rounded-sm border border-line"
            >
              {(['header', 'footer'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={location === l}
                  onClick={() => setLocation(l)}
                  className="px-3 py-1.5 text-sm text-ink-2 aria-pressed:bg-pine-tint aria-pressed:font-medium aria-pressed:text-pine"
                >
                  {t(`admin.menus.locations.${l}`)}
                </button>
              ))}
            </div>
            <HelpTip k="menus.location" />
            {dirty && <Badge tone="amber">{t('admin.menus.unsaved')}</Badge>}
          </div>
          <p className="max-w-[70ch] text-sm text-ink-2">{t(`admin.menus.locationLead.${location}`)}</p>
          {fieldError(save.error, 'items') && (
            <p role="alert" className="text-sm font-medium text-rose">
              {fieldError(save.error, 'items')}
            </p>
          )}
          {items.length === 0 ? (
            <>
              <EmptyState
                title={t('admin.menus.empty')}
                description={t(`admin.menus.emptyLead.${location}`)}
              />
              {canEdit && locale && locales.length > 1 && (
                <StartFromLocale
                  key={`${location}-${locale}`}
                  storeId={storeId}
                  location={location}
                  locale={locale}
                  locales={locales}
                  onLoad={setItems}
                />
              )}
            </>
          ) : (
            <ItemList
              items={items}
              path="items"
              depth={1}
              maxDepth={DEPTH[location]}
              targets={q.data.menus[location].targets}
              categories={cats.data ?? []}
              pages={pageOptions}
              posts={postOptions}
              disabled={!canEdit}
              error={save.error}
              onChange={setItems}
            />
          )}
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button onClick={() => setItems([...items, newItem(t('admin.menus.newItem'))])}>
                <Plus className="h-4 w-4" aria-hidden />
                {t('admin.menus.add')}
              </Button>
              <HelpTip k="menus.add" />
            </div>
          )}
        </>
      )}
    </PageFrame>
  );
}

/**
 * Boş menüyü başka dildeki menüden başlatır: sayfa ve yazı bağlantıları bu dildeki çevirilerine bağlanır,
 * çevirisi olmayanlar çıkarılır. Taslak kaydedilmez; etiketler çevrilip "Menüyü kaydet" ile yayımlanır.
 */
function StartFromLocale({
  storeId,
  location,
  locale,
  locales,
  onLoad,
}: {
  storeId: string;
  location: Location;
  locale: string;
  locales: string[];
  onLoad: (items: Item[]) => void;
}) {
  const t = useT();
  const others = locales.filter((l) => l !== locale);
  const [from, setFrom] = useState(others[0] ?? '');
  const load = useApiMutation(
    () =>
      api.get<MenuTranslationDraft>(
        `/v1/admin/stores/${storeId}/menus/${location}/translation-draft?from=${from}&to=${locale}`,
      ),
    {
      onSuccess: (r) => {
        if (!r.items.length) {
          toast.message(t('admin.menus.draftEmpty', { lang: localeName(from) }));
          return;
        }
        onLoad(r.items);
        if (r.dropped.length)
          toast.message(t('admin.menus.draftLoaded'), {
            description: t('admin.menus.draftDropped', { labels: r.dropped.join(', ') }),
          });
        else toast.success(t('admin.menus.draftLoaded'));
      },
    },
  );
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field
        label={t('admin.menus.startFrom')}
        help={<HelpTip k="menus.translation_source" />}
        className="w-56"
      >
        {(p) => (
          <Select {...p} value={from} onChange={(e) => setFrom(e.target.value)}>
            {others.map((l) => (
              <option key={l} value={l}>
                {localeName(l)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Button disabled={!from || load.isPending} onClick={() => load.mutate(undefined)}>
        <Languages className="h-4 w-4" aria-hidden />
        {t('admin.menus.loadDraft')}
      </Button>
      <HelpTip k="menus.translation_draft" />
    </div>
  );
}

function ItemList({
  items,
  path,
  depth,
  maxDepth,
  targets,
  categories,
  pages,
  posts,
  disabled,
  error,
  onChange,
}: {
  items: Item[];
  path: string;
  depth: number;
  maxDepth: number;
  targets: Targets;
  categories: Category[];
  pages: PageOption[];
  posts: PostOption[];
  disabled: boolean;
  error: unknown;
  onChange: (items: Item[]) => void;
}) {
  const update = (i: number, next: Item | null) =>
    onChange(next ? items.map((x, j) => (j === i ? next : x)) : items.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
    onChange(copy);
  };
  return (
    <ul className={depth === 1 ? 'space-y-3' : 'mt-3 space-y-3 border-s-2 border-line ps-4'}>
      {items.map((it, i) => (
        <li key={it.id}>
          <ItemEditor
            item={it}
            path={`${path}.${i}`}
            depth={depth}
            maxDepth={maxDepth}
            targets={targets}
            categories={categories}
            pages={pages}
            posts={posts}
            disabled={disabled}
            error={error}
            first={i === 0}
            last={i === items.length - 1}
            onChange={(next) => update(i, next)}
            onRemove={() => update(i, null)}
            onMove={(dir) => move(i, dir)}
          />
        </li>
      ))}
    </ul>
  );
}

function ItemEditor({
  item,
  path,
  depth,
  maxDepth,
  targets,
  categories,
  pages,
  posts,
  disabled,
  error,
  first,
  last,
  onChange,
  onRemove,
  onMove,
}: {
  item: Item;
  path: string;
  depth: number;
  maxDepth: number;
  targets: Targets;
  categories: Category[];
  pages: PageOption[];
  posts: PostOption[];
  disabled: boolean;
  error: unknown;
  first: boolean;
  last: boolean;
  onChange: (item: Item) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const t = useT();
  const link = item.link;
  const setLink = (type: Link['type']) => {
    const next: Link =
      type === 'category'
        ? { type, id: categories[0]?.id ?? '' }
        : type === 'product'
          ? { type, id: '' }
          : type === 'page'
            ? { type, id: pages[0]?.id ?? '' }
            : type === 'post'
              ? { type, id: posts[0]?.id ?? '' }
              : type === 'url'
                ? { type, href: '', newTab: false }
                : { type };
    onChange({ ...item, link: next });
  };
  const targetId =
    link.type === 'category' || link.type === 'product' || link.type === 'page' || link.type === 'post'
      ? link.id
      : null;
  const unavailable = targetId ? targets[targetId]?.available === false : false;
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <fieldset
        disabled={disabled}
        className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,12rem)_minmax(0,1.3fr)]"
      >
        <legend className="sr-only">{item.label}</legend>
        <Field
          label={t('admin.menus.label')}
          help={<HelpTip k="menus.label" />}
          error={fieldError(error, `${path}.label`)}
        >
          {(p) => (
            <Input
              {...p}
              value={item.label}
              maxLength={60}
              onChange={(e) => onChange({ ...item, label: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.menus.link')}
          help={<HelpTip k="menus.link" />}
          error={fieldError(error, `${path}.link`)}
        >
          {(p) => (
            <Select {...p} value={link.type} onChange={(e) => setLink(e.target.value as Link['type'])}>
              {LINK_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`admin.menus.linkTypes.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="min-w-0">
          {link.type === 'category' && (
            <Field label={t('admin.menus.category')} help={<HelpTip k="menus.category" />}>
              {(p) => (
                <Select
                  {...p}
                  value={link.id}
                  onChange={(e) => onChange({ ...item, link: { type: 'category', id: e.target.value } })}
                >
                  {!categories.some((c) => c.id === link.id) && (
                    <option value={link.id}>
                      {targets[link.id]?.label ?? t('admin.menus.chooseCategory')}
                    </option>
                  )}
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.status === 'active' ? c.name : t('admin.menus.hiddenCategory', { name: c.name })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {link.type === 'product' && (
            <ProductPicker
              value={link.id}
              currentLabel={targets[link.id]?.label ?? null}
              onPick={(id) => onChange({ ...item, link: { type: 'product', id } })}
            />
          )}
          {link.type === 'page' && (
            <Field label={t('admin.menus.page')} help={<HelpTip k="menus.page" />}>
              {(p) => (
                <Select
                  {...p}
                  value={link.id}
                  onChange={(e) => onChange({ ...item, link: { type: 'page', id: e.target.value } })}
                >
                  {!pages.some((x) => x.id === link.id) && (
                    <option value={link.id}>{targets[link.id]?.label ?? t('admin.menus.choosePage')}</option>
                  )}
                  {pages.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.state === 'live' ? x.title : t('admin.menus.notLivePage', { name: x.title })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {link.type === 'post' && (
            <Field label={t('admin.menus.post')} help={<HelpTip k="menus.post" />}>
              {(p) => (
                <Select
                  {...p}
                  value={link.id}
                  onChange={(e) => onChange({ ...item, link: { type: 'post', id: e.target.value } })}
                >
                  {!posts.some((x) => x.id === link.id) && (
                    <option value={link.id}>{targets[link.id]?.label ?? t('admin.menus.choosePost')}</option>
                  )}
                  {posts.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.state === 'live' ? x.title : t('admin.menus.notLivePage', { name: x.title })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {link.type === 'blog' && <p className="pt-7 text-sm text-ink-3">{t('admin.menus.blogLead')}</p>}
          {link.type === 'url' && (
            <div className="grid gap-2">
              <Field
                label={t('admin.menus.url')}
                help={<HelpTip k="menus.url" />}
                error={fieldError(error, `${path}.link.href`)}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={link.href}
                    placeholder="/iletisim"
                    spellCheck={false}
                    onChange={(e) => onChange({ ...item, link: { ...link, href: e.target.value } })}
                  />
                )}
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={link.newTab}
                  onChange={(e) => onChange({ ...item, link: { ...link, newTab: e.target.checked } })}
                  className="h-4 w-4 accent-[var(--color-pine)]"
                />
                {t('admin.menus.newTab')}
                <HelpTip k="menus.new_tab" />
              </label>
            </div>
          )}
          {link.type === 'none' && <p className="pt-7 text-sm text-ink-3">{t('admin.menus.noneLead')}</p>}
          {link.type === 'products' && (
            <p className="pt-7 text-sm text-ink-3">{t('admin.menus.productsLead')}</p>
          )}
        </div>
      </fieldset>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {unavailable && <Badge tone="amber">{t('admin.menus.unavailable')}</Badge>}
        {unavailable && <HelpTip k="menus.unavailable" />}
        {!disabled && (
          <span className="ms-auto flex flex-wrap items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={first}
              onClick={() => onMove(-1)}
              aria-label={t('admin.menus.moveUp', { label: item.label })}
            >
              <ArrowUp className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={last}
              onClick={() => onMove(1)}
              aria-label={t('admin.menus.moveDown', { label: item.label })}
            >
              <ArrowDown className="h-4 w-4" aria-hidden />
            </Button>
            <HelpTip k="menus.move" />
            {depth < maxDepth && (
              <>
                <Button
                  size="sm"
                  onClick={() =>
                    onChange({ ...item, children: [...item.children, newItem(t('admin.menus.newItem'))] })
                  }
                >
                  {t('admin.menus.addChild')}
                </Button>
                <HelpTip k="menus.add_child" />
              </>
            )}
            <Button size="sm" variant="danger" onClick={onRemove}>
              {t('common.remove')}
            </Button>
            <HelpTip k="menus.remove" />
          </span>
        )}
      </div>
      {item.children.length > 0 && (
        <ItemList
          items={item.children}
          path={`${path}.children`}
          depth={depth + 1}
          maxDepth={maxDepth}
          targets={targets}
          categories={categories}
          pages={pages}
          posts={posts}
          disabled={disabled}
          error={error}
          onChange={(children) => onChange({ ...item, children })}
        />
      )}
    </div>
  );
}

/** Ürün seçici: ada, SKU'ya veya barkoda göre arar; seçilen ürünün adı gösterilir. */
function ProductPicker({
  value,
  currentLabel,
  onPick,
}: {
  value: string;
  currentLabel: string | null;
  onPick: (id: string) => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string | null>(currentLabel);
  const results = useQuery({
    queryKey: ['menu-product-search', q],
    queryFn: () => api.get<ProductPage>(`/v1/admin/products?${new URLSearchParams({ q, limit: '10' })}`),
    enabled: q.trim().length >= 2,
  });
  return (
    <div className="grid gap-2">
      <Field label={t('admin.menus.product')} help={<HelpTip k="menus.product" />}>
        {(p) => (
          <Input
            {...p}
            type="search"
            value={q}
            placeholder={t('admin.menus.productSearch')}
            onChange={(e) => setQ(e.target.value)}
          />
        )}
      </Field>
      {results.data && results.data.items.length > 0 && (
        <ul className="max-h-48 overflow-auto rounded-sm border border-line text-sm">
          {results.data.items.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-start hover:bg-surface-2 aria-pressed:bg-pine-tint"
                aria-pressed={r.id === value}
                onClick={() => {
                  onPick(r.id);
                  setPicked(r.title);
                  setQ('');
                }}
              >
                {r.title}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-sm text-ink-2">
        {value && picked ? t('admin.menus.productPicked', { title: picked }) : t('admin.menus.productNone')}
      </p>
    </div>
  );
}
